const BYTES_PER_WORD = 4;

export const COMPACT_CONTACT_POOL_HEADER_WORDS = 30;
export const COMPACT_CONTACT_POOL_GENERATION_MODE = 0x434f4d50;
export const COMPACT_CONTACT_POOL_RECLAIM_PERIOD = 8;
/** Largest integer epoch that remains exact in Three.js scalar uniforms. */
export const COMPACT_CONTACT_POOL_EPOCH_MAX = 0x00ff_ffff;
export const COMPACT_CONTACT_POOL_RECLAIM_MODE_STRIPE = 1;
export const COMPACT_CONTACT_POOL_RECLAIM_MODE_PRESSURE_FULL = 2;
export const COMPACT_CONTACT_POOL_RECLAIM_MODE_WRAP_FULL = 3;

/** Stable u32 header ABI shared by the compact-contact compute passes. */
export const CompactContactPoolHeaderOffset = Object.freeze({
  manifoldHighWater: 0,
  manifoldFreeHead: 1,
  allocatedManifolds: 2,
  generationMode: 3,
  rowHighWater: 4,
  rowLive: 5,
  rowPeakLive: 6,
  epochParity: 7,
  freeCount0: 8,
  freeCount1: 9,
  freeCursor0: 10,
  freeCursor1: 11,
  fatalBits: 12,
  firstFatalEpoch: 13,
  newRowsThisStep: 14,
  retiredRowsThisStep: 15,
  reclaimCandidateThreads: 16,
  reclaimWorkgroups: 17,
  finalizeCarryThreads: 18,
  finalizeWorkgroups: 19,
  manifoldFreeTail: 20,
  reclaimMode: 21,
  reclaimPhase: 22,
  reclaimPeriod: 23,
  reclaimedManifoldsThisStep: 24,
  reclaimedRowsThisStep: 25,
  pressureFullSweepsThisStep: 26,
  newManifoldsThisStep: 27,
  preReclaimAvailableManifolds: 28,
  preReclaimAvailableRows: 29,
} as const);

/** Sticky fatal bits: never silently continue after losing contact ownership. */
export const CompactContactPoolFatalBit = Object.freeze({
  activeCandidateOverflow: 1 << 0,
  persistentManifoldOverflow: 1 << 1,
  persistentHashProtocolFailure: 1 << 2,
  rowPoolOverflow: 1 << 3,
  duplicateRowOwnership: 1 << 4,
  activeContactListOverflow: 1 << 5,
  pairBodySpillOverflow: 1 << 6,
  endpointDropped: 1 << 7,
  freeListCarryOverflow: 1 << 8,
} as const);

export const COMPACT_CONTACT_POOL_FATAL_ALL = Object.values(CompactContactPoolFatalBit)
  .reduce((mask, bit) => (mask | bit) >>> 0, 0);

export interface CompactContactPoolLayoutInput {
  persistentManifoldHashCapacity: number;
  maxPersistentManifolds: number;
  pairManifoldSlots: number;
  compactContactPoolCapacity: number;
}

export interface CompactContactPoolRange {
  offset: number;
  length: number;
  end: number;
}

export interface CompactContactPoolLayout {
  header: CompactContactPoolRange;
  hashKey: CompactContactPoolRange;
  hashIndex: CompactContactPoolRange;
  poolKey: CompactContactPoolRange;
  poolLastSeen: CompactContactPoolRange;
  poolResumeEpoch: CompactContactPoolRange;
  slotRow: CompactContactPoolRange;
  outputMask: CompactContactPoolRange;
  freeManifolds: CompactContactPoolRange;
  freeRows0: CompactContactPoolRange;
  freeRows1: CompactContactPoolRange;
  totalWords: number;
  totalBytes: number;
}

export interface CompactContactPoolConservationSample {
  compactContactPoolCapacity: number;
  rowHighWater: number;
  rowLive: number;
  nextFreeCount: number;
  /** Optional independent count produced by the active-row compaction pass. */
  activePhysicalRows?: number;
}

export interface CompactContactPoolValidationResult {
  ok: boolean;
  failures: string[];
}

export interface CompactContactPoolSelfTestResult extends CompactContactPoolValidationResult {
  profiles: Array<{
    name: string;
    totalWords: number;
    totalBytes: number;
    compactContactPoolCapacity: number;
  }>;
}

export interface CompactContactPoolStripeWork {
  phase: number;
  period: number;
  candidateThreads: number;
}

export interface CompactContactPoolEpochAdvance {
  epoch: number;
  wrapped: boolean;
}

function positiveSafeInteger(value: number, label: string): number {
  if (!Number.isSafeInteger(value) || value < 1) {
    throw new Error(`${label} must be a positive safe integer (received ${String(value)}).`);
  }
  return value;
}

function checkedProduct(label: string, ...factors: number[]): number {
  let result = 1;
  for (const factor of factors) {
    result *= factor;
    if (!Number.isSafeInteger(result)) throw new Error(`${label} exceeds exact integer range.`);
  }
  return result;
}

function appendRange(cursor: number, length: number, label: string): CompactContactPoolRange {
  const end = cursor + length;
  if (!Number.isSafeInteger(end)) throw new Error(`${label} end exceeds exact integer range.`);
  return Object.freeze({ offset: cursor, length, end });
}

/** CPU mirror of the GPU's deterministic incremental descriptor-stripe schedule. */
export function getCompactContactPoolStripeWork(
  manifoldHighWater: number,
  epoch: number,
  period = COMPACT_CONTACT_POOL_RECLAIM_PERIOD,
): CompactContactPoolStripeWork {
  if (!Number.isSafeInteger(manifoldHighWater) || manifoldHighWater < 0) {
    throw new Error('manifoldHighWater must be a non-negative safe integer.');
  }
  if (!Number.isSafeInteger(epoch) || epoch < 0) {
    throw new Error('epoch must be a non-negative safe integer.');
  }
  positiveSafeInteger(period, 'period');
  const phase = epoch % period;
  const candidateThreads = manifoldHighWater <= phase
    ? 0
    : Math.ceil((manifoldHighWater - phase) / period);
  return { phase, period, candidateThreads };
}

/** Advances the non-zero, float-uniform-exact contact epoch. */
export function advanceCompactContactPoolEpoch(currentEpoch: number): CompactContactPoolEpochAdvance {
  if (
    !Number.isSafeInteger(currentEpoch)
    || currentEpoch < 0
    || currentEpoch > COMPACT_CONTACT_POOL_EPOCH_MAX
  ) {
    throw new Error('currentEpoch exceeds the exact contact epoch range.');
  }
  const raw = currentEpoch + 1;
  return raw > COMPACT_CONTACT_POOL_EPOCH_MAX
    ? { epoch: 1, wrapped: true }
    : { epoch: raw, wrapped: false };
}

/** CPU mirror of the WGSL age calculation for the reserved-zero u32 epoch. */
export function getCompactContactPoolEpochAge(currentEpoch: number, previousEpoch: number): number {
  for (const [value, label] of [
    [currentEpoch, 'currentEpoch'],
    [previousEpoch, 'previousEpoch'],
  ] as const) {
    if (!Number.isSafeInteger(value) || value < 0 || value > COMPACT_CONTACT_POOL_EPOCH_MAX) {
      throw new Error(`${label} exceeds the exact contact epoch range.`);
    }
  }
  return currentEpoch >= previousEpoch
    ? currentEpoch - previousEpoch
    : (COMPACT_CONTACT_POOL_EPOCH_MAX - previousEpoch) + currentEpoch;
}

/**
 * Plans one persistent descriptor map plus one physical contact-row pool.
 * There are two compact free lists, but never two copies of the contact rows.
 */
export function planCompactContactPoolLayout(
  requested: CompactContactPoolLayoutInput,
): CompactContactPoolLayout {
  const hashCapacity = positiveSafeInteger(
    requested.persistentManifoldHashCapacity,
    'persistentManifoldHashCapacity',
  );
  if ((hashCapacity & (hashCapacity - 1)) !== 0) {
    throw new Error('persistentManifoldHashCapacity must be a power of two.');
  }
  const manifolds = positiveSafeInteger(
    requested.maxPersistentManifolds,
    'maxPersistentManifolds',
  );
  const slots = positiveSafeInteger(requested.pairManifoldSlots, 'pairManifoldSlots');
  if (slots > 8) throw new Error('pairManifoldSlots must be <= 8.');
  const capacity = positiveSafeInteger(
    requested.compactContactPoolCapacity,
    'compactContactPoolCapacity',
  );
  const logicalRowLimit = checkedProduct('logical contact row limit', manifolds, slots);
  if (capacity > logicalRowLimit) {
    throw new Error(
      `compactContactPoolCapacity=${capacity} exceeds maxPersistentManifolds * pairManifoldSlots=${logicalRowLimit}.`,
    );
  }

  let cursor = 0;
  const header = appendRange(cursor, COMPACT_CONTACT_POOL_HEADER_WORDS, 'header'); cursor = header.end;
  const hashKey = appendRange(cursor, hashCapacity, 'hashKey'); cursor = hashKey.end;
  const hashIndex = appendRange(cursor, hashCapacity, 'hashIndex'); cursor = hashIndex.end;
  const poolKey = appendRange(cursor, manifolds, 'poolKey'); cursor = poolKey.end;
  const poolLastSeen = appendRange(cursor, manifolds, 'poolLastSeen'); cursor = poolLastSeen.end;
  const poolResumeEpoch = appendRange(cursor, manifolds, 'poolResumeEpoch'); cursor = poolResumeEpoch.end;
  const slotRow = appendRange(cursor, logicalRowLimit, 'slotRow'); cursor = slotRow.end;
  const outputMask = appendRange(cursor, manifolds, 'outputMask'); cursor = outputMask.end;
  const freeManifolds = appendRange(cursor, manifolds, 'freeManifolds'); cursor = freeManifolds.end;
  const freeRows0 = appendRange(cursor, capacity, 'freeRows0'); cursor = freeRows0.end;
  const freeRows1 = appendRange(cursor, capacity, 'freeRows1'); cursor = freeRows1.end;

  return Object.freeze({
    header,
    hashKey,
    hashIndex,
    poolKey,
    poolLastSeen,
    poolResumeEpoch,
    slotRow,
    outputMask,
    freeManifolds,
    freeRows0,
    freeRows1,
    totalWords: cursor,
    totalBytes: checkedProduct('compact contact map byte length', cursor, BYTES_PER_WORD),
  });
}

/** Checks the post-finalize ownership invariant without touching WebGPU. */
export function validateCompactContactPoolConservation(
  sample: CompactContactPoolConservationSample,
): CompactContactPoolValidationResult {
  const failures: string[] = [];
  const values = [
    ['compactContactPoolCapacity', sample.compactContactPoolCapacity],
    ['rowHighWater', sample.rowHighWater],
    ['rowLive', sample.rowLive],
    ['nextFreeCount', sample.nextFreeCount],
  ] as const;
  for (const [label, value] of values) {
    if (!Number.isSafeInteger(value) || value < 0) failures.push(`${label} is not a non-negative safe integer`);
  }
  if (failures.length === 0) {
    const capacity = sample.compactContactPoolCapacity;
    if (capacity < 1) failures.push('compactContactPoolCapacity must be positive');
    if (sample.rowHighWater > capacity) failures.push('rowHighWater exceeds capacity');
    if (sample.rowLive > sample.rowHighWater) failures.push('rowLive exceeds rowHighWater');
    if (sample.nextFreeCount > capacity) failures.push('nextFreeCount exceeds capacity');
    if (sample.rowLive + sample.nextFreeCount !== sample.rowHighWater) {
      failures.push('rowLive + nextFreeCount does not equal rowHighWater');
    }
    if (
      sample.activePhysicalRows !== undefined
      && (!Number.isSafeInteger(sample.activePhysicalRows) || sample.activePhysicalRows < 0)
    ) {
      failures.push('activePhysicalRows is not a non-negative safe integer');
    } else if (
      sample.activePhysicalRows !== undefined
      && sample.activePhysicalRows !== sample.rowLive
    ) {
      failures.push('activePhysicalRows does not equal rowLive');
    }
  }
  return { ok: failures.length === 0, failures };
}

/** Literal, GPU-free ABI and ownership checks used by the capacity planner. */
export function runCompactContactPoolSelfTests(): CompactContactPoolSelfTestResult {
  const failures: string[] = [];
  const profiles: CompactContactPoolSelfTestResult['profiles'] = [];
  const cases = [
    {
      name: 'coliseum50k',
      input: {
        persistentManifoldHashCapacity: 524_288,
        maxPersistentManifolds: 196_608,
        pairManifoldSlots: 8,
        compactContactPoolCapacity: 786_432,
      },
      totalWords: 5_177_374,
      totalBytes: 20_709_496,
    },
    {
      name: 'coliseum64k',
      input: {
        persistentManifoldHashCapacity: 524_288,
        maxPersistentManifolds: 262_144,
        pairManifoldSlots: 8,
        compactContactPoolCapacity: 917_504,
      },
      totalWords: 6_291_486,
      totalBytes: 25_165_944,
    },
  ] as const;
  for (const test of cases) {
    const layout = planCompactContactPoolLayout(test.input);
    profiles.push({
      name: test.name,
      totalWords: layout.totalWords,
      totalBytes: layout.totalBytes,
      compactContactPoolCapacity: test.input.compactContactPoolCapacity,
    });
    if (layout.totalWords !== test.totalWords) {
      failures.push(`${test.name}.totalWords: expected ${test.totalWords}, received ${layout.totalWords}`);
    }
    if (layout.totalBytes !== test.totalBytes) {
      failures.push(`${test.name}.totalBytes: expected ${test.totalBytes}, received ${layout.totalBytes}`);
    }
    const ranges = [
      layout.header,
      layout.hashKey,
      layout.hashIndex,
      layout.poolKey,
      layout.poolLastSeen,
      layout.poolResumeEpoch,
      layout.slotRow,
      layout.outputMask,
      layout.freeManifolds,
      layout.freeRows0,
      layout.freeRows1,
    ];
    for (let index = 0; index < ranges.length; index++) {
      const expectedOffset = index === 0 ? 0 : ranges[index - 1].end;
      if (ranges[index].offset !== expectedOffset) failures.push(`${test.name}: non-contiguous range ${index}`);
    }
    if (ranges[ranges.length - 1].end !== layout.totalWords) {
      failures.push(`${test.name}: final range does not end at totalWords`);
    }
  }

  const validConservation = validateCompactContactPoolConservation({
    compactContactPoolCapacity: 16,
    rowHighWater: 12,
    rowLive: 7,
    nextFreeCount: 5,
    activePhysicalRows: 7,
  });
  if (!validConservation.ok) failures.push('valid capacity conservation sample was rejected');
  const invalidConservation = validateCompactContactPoolConservation({
    compactContactPoolCapacity: 16,
    rowHighWater: 12,
    rowLive: 8,
    nextFreeCount: 5,
  });
  if (invalidConservation.ok) failures.push('invalid capacity conservation sample was accepted');
  if (
    Object.values(CompactContactPoolHeaderOffset).some((offset, index) => offset !== index)
    || Object.values(CompactContactPoolHeaderOffset).length !== COMPACT_CONTACT_POOL_HEADER_WORDS
  ) {
    failures.push('compact contact header offsets are not the contiguous 30-word ABI');
  }
  if (COMPACT_CONTACT_POOL_FATAL_ALL !== 0x1ff) {
    failures.push('compact contact fatal-bit mask changed unexpectedly');
  }

  const stripeHighWater = 19;
  const stripeVisits = new Array<number>(stripeHighWater).fill(0);
  for (let epoch = 0; epoch < COMPACT_CONTACT_POOL_RECLAIM_PERIOD; epoch++) {
    const work = getCompactContactPoolStripeWork(stripeHighWater, epoch);
    for (let gid = 0; gid < work.candidateThreads; gid++) {
      const poolIndex = work.phase + gid * work.period;
      if (poolIndex >= stripeHighWater) {
        failures.push('stripe work emitted an index beyond stable high-water');
      } else {
        stripeVisits[poolIndex]++;
      }
    }
  }
  if (stripeVisits.some((visits) => visits !== 1)) {
    failures.push('one reclaim period did not visit every stable descriptor exactly once');
  }
  const oneStripe = getCompactContactPoolStripeWork(stripeHighWater, 7, 1);
  if (oneStripe.phase !== 0 || oneStripe.candidateThreads !== stripeHighWater) {
    failures.push('N=1 stripe schedule is not equivalent to a full stable-high-water scan');
  }
  const wrappedEpoch = advanceCompactContactPoolEpoch(COMPACT_CONTACT_POOL_EPOCH_MAX);
  if (wrappedEpoch.epoch !== 1 || !wrappedEpoch.wrapped) {
    failures.push('u32 epoch wrap did not skip reserved zero and request wrap maintenance');
  }
  if (
    getCompactContactPoolEpochAge(1, COMPACT_CONTACT_POOL_EPOCH_MAX) !== 1
    || getCompactContactPoolEpochAge(1, COMPACT_CONTACT_POOL_EPOCH_MAX - 1) !== 2
  ) {
    failures.push('reserved-zero epoch wrap changed compact stale age');
  }

  for (const mutate of [
    () => planCompactContactPoolLayout({ ...cases[0].input, persistentManifoldHashCapacity: 3 }),
    () => planCompactContactPoolLayout({ ...cases[0].input, pairManifoldSlots: 9 }),
    () => planCompactContactPoolLayout({ ...cases[0].input, compactContactPoolCapacity: 2_000_000 }),
  ]) {
    let rejected = false;
    try { mutate(); } catch { rejected = true; }
    if (!rejected) failures.push('invalid compact contact layout was accepted');
  }

  return { ok: failures.length === 0, profiles, failures };
}
